import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {
  get,
  find,
} from 'lodash';
import {
  CircularProgress,
  Dialog,
  Popper,
} from '@mui/material';
import API from '../../api';
import VibeIcon from '../VibeIcon/VibeIcon';
import viStar from '../../icons/viStar';
import viStarOutline from '../../icons/viStarOutline';
import viClose from '../../icons/viClose';
import viTag from '../../icons/viTag';
import ManageTags from './ManageTags';
import Tag from './Tag';
import color from '../../sass/color.scss';
import './Tags.scss';

class Tags extends PureComponent {
  constructor(props) {
    super(props);

    const {
      user,
      tags = [],
      onlyAdminTags,
    } = props;

    // Input container ref
    this.inputContainerRef = React.createRef();
    // Input ref
    this.inputRef = React.createRef();
    // Tag results ref
    this.tagResultsRef = React.createRef();
    // Original tags
    this.originalTags = [];
    // Original tags were fetched
    this.originalTagsComplete = false;
    // Timeout to run API fetch for tags
    this.fetchTimeout = null;
    // Delay (ms) to wait before running fetch
    this.fetchTimeoutDelay = 500;

    const canManageTags = user.can('tag.view_admin') || user.can('tag.view');
    const canViewAdminTags = user.can('tag.view_admin');
    const canViewClientTags = user.can('tag.view');
    const canCreateAdminTags = user.can('tag.create_admin');
    const canCreateClientTags = user.can('tag.create');

    this.state = {
      // Can the user manage the tags at all
      canManageTags,
      // Can the user view (manage) admin tags
      canViewAdminTags,
      // Can the user view (manage) client tags
      canViewClientTags,
      // Can the user create admin tags
      canCreateAdminTags,
      // Can the user create client tags
      canCreateClientTags,
      // Is the modal to manage tags open
      isManageTagsOpen: false,
      // Is the input looking for admin tags (only default admin tags if they can't view client tags)
      isAdminTags: (user.can('tag.view_admin') && !user.can('tag.view'))
        || (user.can('tag.view_admin') && onlyAdminTags),
      // When fetching tags
      isFetching: false,
      // Results from the search input fetch
      tagResults: [],
      // Show the tag results
      showTagResults: false,
      // When the input has focus
      inputFocus: false,
      // Index of tag in result list to highlight
      resultIndex: -1,
      // List of tags currently being added
      addedTags: tags,
    };
  }

  componentDidMount() {
    const {
      showTagsInline,
    } = this.props;

    const {
      inputContainerRef: {
        current: inputContainerRef,
      },
    } = this;

    if (showTagsInline && inputContainerRef) {
      // Scroll to the right
      inputContainerRef.scrollLeft = inputContainerRef.scrollWidth;
    }
  }

  componentDidUpdate(prevProps) {
    const {
      dataId,
      onlyAdminTags,
      onRemove,
    } = this.props;

    const {
      onlyAdminTags: prevOnlyAdminTags,
    } = prevProps;

    if (onlyAdminTags !== prevOnlyAdminTags) {
      this.setState((state) => {
        const addedTags = onlyAdminTags
          ? state.addedTags.filter(addedTag => addedTag.type === 'admin')
          : state.addedTags;

        const removedTags = onlyAdminTags
          ? state.addedTags.filter(addedTag => addedTag.type !== 'admin')
          : [];

        // remove tags if only allowing admin and there are existing client tags
        removedTags.forEach((tag) => {
          onRemove(tag, dataId);
        });

        return {
          isAdminTags: onlyAdminTags,
          addedTags,
        };
      });
    }
  }

  componentWillUnmount() {
    // Clear the fetch timeout before component unmounts
    clearTimeout(this.fetchTimeout);
  }

  /**
   * When the search input is changed
   */
  onChange = (e) => {
    const {
      onChange,
    } = this.props;

    const {
      target: {
        value = '',
      },
    } = e;

    onChange(e);

    this.setState({
      isFetching: true,
      tagResults: [],
      showTagResults: value !== '',
      resultIndex: 0,
    });

    clearTimeout(this.fetchTimeout);
    this.fetchTimeout = setTimeout(this.fetchTags, this.fetchTimeoutDelay);
  };

  /**
   * When the search input has focus
   */
  onFocus = (e) => {
    const {
      value,
      onFocus,
    } = this.props;

    this.fetchTags();

    this.setState({
      inputFocus: true,
      showTagResults: true,
      resultIndex: value === ''
        ? -1
        : 0,
    });

    onFocus(e);
  };

  /**
   * When the search input is blurred
   */
  onBlur = (e) => {
    const {
      onBlur,
    } = this.props;

    this.setState({
      inputFocus: false,
      resultIndex: -1,
    });

    // Wait to hide results in case one was clicked
    setTimeout(() => {
      this.setState({
        showTagResults: false,
      });
    }, 500);

    onBlur(e);
  };

  /**
   * When the search input key is pressed
   */
  onKeyDown = (e) => {
    const {
      onKeyDown,
    } = this.props;

    const {
      tagResults,
      resultIndex,
    } = this.state;

    const {
      inputRef: {
        current: inputRef,
      },
    } = this;

    const showTagResults = tagResults.length > 0
      && (e.key === 'ArrowDown' || e.key === 'ArrowUp');

    switch (e.key) {
      // Go to the next item in the autocomplete
      case 'ArrowDown': {
        e.preventDefault();

        this.setState((state) => {
          const {
            tagResultsRef: {
              current: tagResultsRef,
            },
          } = this;

          const resultIndex = state.resultIndex < (state.tagResults.length - 1)
            ? state.resultIndex + 1
            : (state.tagResults.length - 1);

          if (tagResultsRef) {
            // Get the center scroll position of the selected result index
            const scrollTop = (40 * resultIndex) - 80;

            tagResultsRef.scrollTop = scrollTop < 0
              ? 0
              : scrollTop;
          }

          return {
            showTagResults,
            // Always set result index to 0 if show tag results is just opened
            resultIndex,
          };
        });

        break;
      }

      // Go to the previous item in the autocomplete
      case 'ArrowUp': {
        e.preventDefault();

        this.setState((state) => {
          const {
            tagResultsRef: {
              current: tagResultsRef,
            },
          } = this;

          const resultIndex = state.resultIndex > 0
            ? state.resultIndex - 1
            : 0;

          if (tagResultsRef) {
            // Get the center scroll position of the selected result index
            const scrollTop = (40 * resultIndex) - 80;

            tagResultsRef.scrollTop = scrollTop < 0
              ? 0
              : scrollTop;
          }

          return {
            showTagResults,
            resultIndex,
          };
        });

        break;
      }

      // Add the tag
      case 'Enter': {
        this.addSelectedTag(resultIndex);

        if (inputRef) {
          inputRef.focus();
        }
        break;
      }

      default:
        break;
    }

    onKeyDown(e);
  };

  /**
   * When the manage tags is canceled
   */
  onCancelManageTags = () => {
    // On next fetch, save the intial tags since we switched between admin/client
    this.originalTags = [];
    this.originalTagsComplete = false;

    this.setState({
      isManageTagsOpen: false,
    }, this.fetchTags);
  };

  /**
   * When the manage tags save button is clicked
   */
  onSaveManageTags = () => {
    // On next fetch, save the intial tags since we switched between admin/client
    this.originalTags = [];
    this.originalTagsComplete = false;

    this.setState({
      isManageTagsOpen: false,
    }, this.fetchTags);
  };

  /**
   * When a tag is clicked from the tag results list
   */
  onClickTagResult = (e) => {
    const {
      target: {
        dataset: {
          index,
        },
      },
    } = e;

    // Add the selected tag to the object
    this.addSelectedTag(index);
  };

  /**
   * When an added tag is removed
   */
  onRemoveAddedTag = (tag) => {
    const {
      dataId,
      onRemove,
    } = this.props;

    const {
      inputRef: {
        current: inputRef,
      },
    } = this;

    if (inputRef) {
      inputRef.focus();
    }

    this.setState((state) => {
      return {
        addedTags: state.addedTags.filter(addedTag => addedTag._id !== tag._id),
      };
    });

    onRemove(tag, dataId);
  };

  /**
   * Fetch tags
   */
  fetchTags = async () => {
    const {
      assign,
      value,
      companyId,
      disabled,
      onlyAdminTags,
      user,
    } = this.props;

    const {
      isAdminTags,
      canViewAdminTags,
      canCreateAdminTags,
      canCreateClientTags,
    } = this.state;

    const {
      ADMIN_COMPANY_ID: adminCompanyId,
    } = process.env;

    if (!assign
      || disabled
      || (!companyId && (!canViewAdminTags && onlyAdminTags))
      || !user.hasAccessToCompany(companyId)) {
      // No company ID provided, field is disabled, or locked to admin tags and user cannot view admin tags
      // or the object is assigned to a company the user doesn't belong to
      return;
    }

    const itemCompanyId = companyId || user.companyId;

    const filterCompanyId = isAdminTags
      ? adminCompanyId
      : itemCompanyId;

    const filters = {
      companyId: filterCompanyId,
      type: isAdminTags
        ? 'admin'
        : 'client',
    };

    // Only include name if a vlue is provided
    if (value) {
      filters.name = value;
    }

    const tags = await API.Tag.list({
      sort: {
        name: 'asc',
      },
      filters,
      pageSize: -1,
    });

    const data = get(tags, 'data', []);

    // Save original tags when the autocomplete is re-rendered
    if (!this.originalTagsComplete) {
      this.originalTags = data;
      this.originalTagsComplete = true;
    }

    // Check to see if the tag exists
    const tagExists = data.filter(tag => tag.name.toLowerCase() === value.toLowerCase())[0] !== undefined;
    const showCreateTag = !tagExists
      && ((value && isAdminTags && canCreateAdminTags) || (value && !isAdminTags && canCreateClientTags));

    if (showCreateTag) {
      // Add the create tag to the results
      data.unshift({
        _id: 'create-tag',
        name: `Create ${value}...`,
        isCreate: true,
      });
    }

    this.setState({
      isFetching: false,
      tagResults: data,
    });
  };

  /**
   * Add the selected tag to the object
   */
  addSelectedTag = async (index) => {
    const {
      companyId,
      dataId,
      value,
      showTagsInline,
      onAdd,
    } = this.props;

    const {
      tagResults,
      isAdminTags,
    } = this.state;

    const {
      inputContainerRef: {
        current: inputContainerRef,
      },
    } = this;

    const {
      ADMIN_COMPANY_ID: adminCompanyId,
    } = process.env;

    const selectedTag = get(tagResults, `[${index}]`, null);
    let addedTag;

    if (selectedTag) {
      if (selectedTag.isCreate) {
        // Create the tag before adding it
        const saveCompanyId = isAdminTags
          ? adminCompanyId
          : companyId;

        const result = await API.Tag.create({
          name: value,
          type: isAdminTags
            ? 'admin'
            : 'client',
          companyId: saveCompanyId,
        });

        const responseType = get(result, '[0].type', null);
        const newTag = get(result, '[0].data', {});
        newTag._id = get(result, '[0].documentId', null);

        if (responseType === 'TAG.CREATED') {
          // Send the created tag to the callback component
          addedTag = newTag;
        } else {
          console.error('Error creating tag', result);
        }
      } else {
        addedTag = selectedTag;
      }

      this.setState((state) => {
        const exists = find(state.addedTags, { type: addedTag.type, name: addedTag.name });

        return {
          addedTags: !exists
            ? [...state.addedTags, addedTag]
            : state.addedTags,
          showTagResults: false,
          resultIndex: -1,
          tagResults: this.originalTags,
        };
      }, () => {
        if (showTagsInline && inputContainerRef) {
          // Scroll to the right
          inputContainerRef.scrollLeft = inputContainerRef.scrollWidth;
        }
      });

      onAdd(addedTag, dataId);

      // Clear the input
      this.clearSearch();
    }
  };

  /**
   * Clear search input
   */
  clearSearch = () => {
    const {
      onChange,
    } = this.props;

    // Clear the input
    onChange({
      target: {
        value: '',
      },
    });
  };

  /**
   * Toggle manage tags open and closed
   */
  toggleManageTagsModal = () => {
    this.setState((state) => {
      return {
        isManageTagsOpen: !state.isManageTagsOpen,
      };
    });
  };

  /**
   * Toggle viewing admin/client tags
   */
  toggleAdminTags = () => {
    const {
      onlyAdminTags,
    } = this.props;

    const {
      isAdminTags,
      canViewClientTags,
    } = this.state;

    if (isAdminTags && !canViewClientTags) {
      console.error('User cannot view client tags');
      return;
    }

    if (onlyAdminTags) {
      console.warn('Only admin tags enabled');
      return;
    }

    // On next fetch, save the initial tags since we switched between admin/client
    this.originalTags = [];
    this.originalTagsComplete = false;

    this.setState((state) => {
      return {
        isAdminTags: !state.isAdminTags,
      };
    });
  };

  render() {
    const {
      user,
      id,
      dataId,
      className,
      label,
      hideLabel,
      assign,
      showTagsInline,
      showTagsList,
      companyId,
      tags,
      style,
      tabIndex,
      placeholder = 'Press the down arrow to see available tags...',
      value,
      autoFocus,
      autoComplete,
      required,
      disabled,
      onlyAdminTags,
      onRemove,
    } = this.props;

    const {
      canManageTags,
      canViewAdminTags,
      isManageTagsOpen,
      isAdminTags,
      isFetching,
      tagResults,
      inputFocus,
      showTagResults,
      resultIndex,
      addedTags,
    } = this.state;

    const {
      inputContainerRef: {
        current: inputContainerRef,
      },
    } = this;

    // Check if we need to disable the tag input
    const isDisabled = disabled
      || !assign
      || (!canViewAdminTags && onlyAdminTags)
      || !user.hasAccessToCompany(companyId);

    const resultWidth = showTagResults
      ? get(inputContainerRef.getBoundingClientRect(), 'width', 200)
      : 0;

    return (
      <div className="Tags">
        {!hideLabel && (label || canManageTags) ? (
          <div className="label-container">
            {label ? (
              <label
                className="field-label"
                htmlFor={`tag-${id}`}
              >
                {isAdminTags
                  ? 'Admin Tags'
                  : 'User Tags'}

                {required ? (
                  <span className="field-dot" />
                ) : null}
              </label>
            ) : null}

            {canManageTags && companyId && !disabled ? (
              <div className="manage-tags">
                <span
                  className="manage-tags-text"
                  onClick={this.toggleManageTagsModal}
                >
                  Manage Tags
                </span>
              </div>
            ) : null}
          </div>
        ) : null}

        <div
          ref={this.inputContainerRef}
          className={classNames('tags-input-container', {
            'show-admin': canViewAdminTags,
            focus: inputFocus,
          })}
        >
          {canViewAdminTags ? (
            <div className="admin-tag-select">
              <VibeIcon
                type="button"
                icon={isAdminTags
                  ? viStar
                  : viStarOutline}
                color={color.manatee}
                size={24}
                tooltip={isAdminTags
                  ? 'Switch to User Tags'
                  : 'Switch to Admin Tags'}
                buttonProps={{
                  size: 32,
                  color: color.lightGray,
                  borderColor: color.whiteSmoke,
                  style: {
                    borderTopRightRadius: 0,
                    borderBottomRightRadius: 0,
                    borderBottomLeftRadius: showTagResults
                      ? 0
                      : 4,
                    border: 'none',
                    borderRight: inputFocus
                      ? `1px solid ${color.violetVibe}`
                      : `1px solid ${color.whiteSmoke}`,
                  },
                }}
                onClick={this.toggleAdminTags}
              />
            </div>
          ) : null}

          {showTagsInline && addedTags.length > 0 ? (
            <div className="added-tags">
              {addedTags.map((tag, index) => {
                return (
                  <Tag
                    key={`tag-${index}`}
                    tag={tag}
                    dataId={dataId}
                    allowRemove={user.hasAccessToCompany(companyId)}
                    onRemove={this.onRemoveAddedTag}
                  />
                );
              })}
            </div>
          ) : null}

          <input
            ref={this.inputRef}
            type="text"
            id={`tag-${id}`}
            className={classNames('tags-field', className, {
              focus: inputFocus,
            })}
            tabIndex={tabIndex}
            placeholder={isDisabled ? 'You are unable to add tags...' : placeholder}
            value={value}
            autoFocus={autoFocus}
            autoComplete={autoComplete}
            disabled={isDisabled || (!isAdminTags && !companyId)}
            onChange={this.onChange}
            onKeyDown={this.onKeyDown}
            onFocus={this.onFocus}
            onBlur={this.onBlur}
          />

          {value ? (
            <div className="clear-search">
              <VibeIcon
                className="clear-search-icon"
                icon={viClose}
                color={color.manatee}
                size={20}
                onClick={this.clearSearch}
              />
            </div>
          ) : null}

          {showTagResults ? (
            <Popper
              anchorEl={inputContainerRef}
              className="TagsPopper"
              open
              placement="bottom-start"
              onClick={this.onClick}
            >
              <div
                ref={this.tagResultsRef}
                className="TagsPopperResults"
                style={{
                  width: resultWidth,
                  height:
                    tagResults.length < 5
                      ? (40 * tagResults.length) + 2
                      : 200,
                }}
              >
                {tagResults.map((tag, index) => {
                  return (
                    <div
                      key={tag._id}
                      className={classNames('tag-result', {
                        selected: resultIndex === index,
                        create: tag.isCreate,
                      })}
                      data-index={index}
                      onClick={this.onClickTagResult}
                    >
                      <div
                        className="tag-name"
                        style={{
                          fontWeight:
                            tag.isCreate
                              ? 700
                              : 400,
                          color:
                            tag.isCreate
                              ? color.violetVibe
                              : 'inherit',
                        }}
                        data-index={index}
                      >
                        {tag.name}
                      </div>
                    </div>
                  );
                })}

                {isFetching ? (
                  <div className="loading">
                    <CircularProgress
                      className="loading-icon"
                      color="inherit"
                      size={30}
                    />
                  </div>
                ) : null}

                {!isFetching && tagResults.length <= 0 ? (
                  <div className="empty-results">
                    No tags found matching {value}...
                  </div>
                ) : null}
              </div>
            </Popper>
          ) : null}
        </div>

        {showTagsList && tags.length > 0 ? (
          <div className="tags-list">
            <div className="tags-icon-container">
              <VibeIcon
                type="button"
                icon={viTag}
                color={color.manatee}
                size={16}
                buttonProps={{
                  size: 24,
                  color: color.white,
                  borderColor: color.whiteSmoke,
                }}
              />
            </div>

            {tags.map((tag) => {
              // Hide removed tags from the list under the input
              if (tag.status === 'remove') {
                return null;
              }

              return (
                <Tag
                  key={tag._id}
                  tag={tag}
                  dataId={dataId}
                  style={{
                    marginTop: 10,
                    marginRight: 8,
                    ...style,
                  }}
                  allowRemove={!disabled
                    && assign
                    && user.hasAccessToCompany(companyId)}
                  onRemove={onRemove}
                />
              );
            })}
          </div>
        ) : null}

        {!disabled ? (
          <Dialog
            className="TagsManageDialog"
            classes={{
              paperFullWidth: 'TagsManageDialogPaper',
            }}
            open={isManageTagsOpen}
            fullWidth
            // disableEnforceFocus
          >
            <ManageTags
              companyId={companyId}
              onCancel={this.onCancelManageTags}
              onSave={this.onSaveManageTags}
            />
          </Dialog>
        ) : null}
      </div>
    );
  }
}

Tags.propTypes = {
  /** ID of item with tags */
  id: PropTypes.string,
  /** Field ID for data attribute on input */
  dataId: PropTypes.string,
  /** Custom class */
  className: PropTypes.string,
  /** Label for field */
  label: PropTypes.string,
  /** Force hide the label area */
  hideLabel: PropTypes.bool,
  /** Only allow admin tags */
  onlyAdminTags: PropTypes.bool,
  /** Show tags inline with the input box */
  showTagsInline: PropTypes.bool,
  /** Show tags list below the field */
  showTagsList: PropTypes.bool,
  /** Show the assign tags input */
  assign: PropTypes.bool,
  /** Company ID to fetch tags */
  companyId: PropTypes.string,
  /** Current tags on the object */
  tags: PropTypes.arrayOf(PropTypes.shape({
    _id: PropTypes.string,
    active: PropTypes.bool,
    name: PropTypes.string,
    type: PropTypes.string,
  })),
  /** Custom style */
  style: PropTypes.oneOfType([
    PropTypes.object,
  ]),
  /** Tab index */
  tabIndex: PropTypes.number,
  /** Placeholder text */
  placeholder: PropTypes.string,
  /** Value of field */
  value: PropTypes.string,
  /** Auto focus the input field */
  autoFocus: PropTypes.bool,
  /** Auto complete the input field */
  autoComplete: PropTypes.string,
  /** Field is required */
  required: PropTypes.bool,
  /** Disable the input field */
  disabled: PropTypes.bool,
  /** When the field is changed */
  onChange: PropTypes.func,
  /** When the field has a key pressed */
  onKeyDown: PropTypes.func,
  /** When the field is focused */
  onFocus: PropTypes.func,
  /** When the field is blurred */
  onBlur: PropTypes.func,
  /** When a tag is added */
  onAdd: PropTypes.func,
  /** When a tag is removed */
  onRemove: PropTypes.func,
};

Tags.defaultProps = {
  id: 'new-tag',
  dataId: null,
  className: '',
  label: 'Tags',
  hideLabel: false,
  onlyAdminTags: false,
  showTagsInline: false,
  showTagsList: true,
  assign: false,
  companyId: null,
  tags: [],
  style: {},
  tabIndex: -1,
  placeholder: 'Input Tags',
  value: '',
  autoFocus: false,
  autoComplete: 'on',
  required: false,
  disabled: false,
  onChange: () => {},
  onKeyDown: () => {},
  onFocus: () => {},
  onBlur: () => {},
  onAdd: () => {},
  onRemove: () => {},
};

function mapStateToProps(state) {
  return {
    user: state.login.user,
  };
}

export default connect(mapStateToProps)(Tags);
